// Afspraak invoeren of wijzigen (spec A1). A repeating appointment opens on one occurrence:
// "Alleen deze keer" is the default, "Deze en volgende" and "Deze keer overslaan" are next to it.
import { useState, useSyncExternalStore, type FormEvent } from "react";
import { getMeta } from "../lib/db";
import type { Me } from "../lib/session";
import { useLiveQuery } from "dexie-react-hooks";
import { toast } from "sonner";
import { repeatOf, type Occurrence, type Repeat } from "@shared/recurrence";
import { displayDate, planningDate } from "@shared/time";
import {
  removeAppointment,
  saveAppointment,
  saveFromOccurrence,
  saveOccurrence,
  skipOccurrence,
  type AppointmentForm,
} from "../lib/appointments";
import { db, type Appointment } from "../lib/db";
import { Sheet } from "./Sheet";
import { Button, cn } from "./ui";

type OpenState = { kind: "new"; date: string } | { kind: "occurrence"; occurrence: Occurrence } | { kind: "series"; appointmentId: string };

let state: OpenState | null = null;
const listeners = new Set<() => void>();
export function openAppointment(next: OpenState | null) {
  state = next;
  listeners.forEach((l) => l());
}
function useOpen() {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => state,
  );
}

const WEEKDAYS = ["ma", "di", "wo", "do", "vr", "za", "zo"];
const DURATIONS = [15, 30, 45, 60, 90, 120, 180, 240];
const REMINDERS = [0, 5, 15, 30, 60];
const TRAVEL = [0, 10, 15, 20, 30, 45, 60, 90];

function durationLabel(m: number) {
  if (m < 60) return `${m} minuten`;
  const h = Math.floor(m / 60);
  const rest = m % 60;
  return rest ? `${h} uur ${rest} min` : h === 1 ? "1 uur" : `${h} uur`;
}

function isoWeekdayOf(date: string) {
  const d = new Date(`${date}T00:00:00Z`).getUTCDay();
  return d === 0 ? 7 : d;
}

function formFrom(a: Appointment, o: Occurrence | undefined, sharedSpaceId: string | null): AppointmentForm {
  const start = (o?.startLocal ?? a.start_local).replace(" ", "T");
  const end = o ? (Date.parse(`${o.endLocal}Z`) - Date.parse(`${o.startLocal}Z`)) / 60000 : a.duration_minutes;
  return {
    title: o?.title ?? a.title,
    location: (o ? o.location : a.location) ?? "",
    notes: a.notes ?? "",
    date: start.slice(0, 10),
    time: start.slice(11, 16),
    durationMinutes: end,
    repeat: repeatOf(a),
    remindMinutes: a.remind_minutes,
    travelMinutes: a.travel_minutes ?? 0,
    who: a.space_id === sharedSpaceId ? "together" : (a.visibility ?? "private"),
  };
}

const WHO = [
  ["private", "Alleen ik"],
  ["busy", "Bezet"],
  ["shared", "Zichtbaar"],
  ["together", "Samen"],
] as const;

export function AppointmentSheetHost() {
  const open = useOpen();
  if (!open) return null;
  const key =
    open.kind === "new"
      ? `new-${open.date}`
      : open.kind === "series"
        ? open.appointmentId
        : `${open.occurrence.appointmentId}-${open.occurrence.recurrenceLocal}`;
  return <AppointmentSheet key={key} open={open} />;
}

function AppointmentSheet({ open }: { open: OpenState }) {
  const appointmentId = open.kind === "occurrence" ? open.occurrence.appointmentId : open.kind === "series" ? open.appointmentId : null;
  const appointment = useLiveQuery(
    async () => (appointmentId ? ((await db.appointments.get(appointmentId)) ?? null) : null),
    [appointmentId],
  );
  const me = useLiveQuery(async () => (await getMeta<Me>("me")) ?? null);
  if ((appointmentId && appointment === undefined) || me === undefined) return null;
  return <AppointmentFormSheet open={open} appointment={appointment ?? null} me={me} />;
}

function AppointmentFormSheet({ open, appointment, me }: { open: OpenState; appointment: Appointment | null; me: Me | null }) {
  const household = me?.household ?? [];
  const other = household.find((h) => h.id !== me?.user.id);
  const changedBy =
    appointment?.updated_by && appointment.updated_by !== me?.user.id
      ? household.find((h) => h.id === appointment.updated_by)?.name
      : undefined;
  const occurrence = open.kind === "occurrence" ? open.occurrence : undefined;
  const repeating = !!appointment?.rrule;
  const [seriesMode, setSeriesMode] = useState(open.kind !== "occurrence" || !repeating);
  const [form, setForm] = useState<AppointmentForm>(() =>
    appointment
      ? formFrom(appointment, occurrence, me?.settings?.shared_space_id ?? null)
      : {
          title: "",
          location: "",
          notes: "",
          date: open.kind === "new" ? open.date : planningDate(new Date()),
          time: "10:00",
          durationMinutes: 60,
          repeat: { kind: "none", interval: 1, weekdays: [], until: null },
          remindMinutes: 15,
          travelMinutes: 0,
          // PK keeps his own private; Tsjits shows she is busy (B43).
          who: me?.user.role === "member" ? "busy" : "private",
        },
  );
  const [error, setError] = useState<string | null>(null);
  const close = () => openAppointment(null);
  const set = <K extends keyof AppointmentForm>(key: K, value: AppointmentForm[K]) => setForm((f) => ({ ...f, [key]: value }));
  const setRepeat = (patch: Partial<Repeat>) => setForm((f) => ({ ...f, repeat: { ...f.repeat, ...patch } }));

  function validate(): boolean {
    if (!form.title.trim()) return fail("Geef de afspraak een titel.");
    if (!form.date || !form.time) return fail("Kies een datum en tijd.");
    if (seriesMode && form.repeat.kind === "weekly" && !form.repeat.weekdays.length) return fail("Kies op welke dagen hij terugkomt.");
    setError(null);
    return true;
  }
  function fail(message: string) {
    setError(message);
    return false;
  }

  async function submit(event: FormEvent, scope: "series" | "this" | "following") {
    event.preventDefault();
    if (!validate()) return;
    try {
      if (scope === "this" && occurrence) {
        await saveOccurrence(occurrence, form);
        toast("Alleen deze keer aangepast");
      } else if (scope === "following" && occurrence && appointment) {
        await saveFromOccurrence(appointment, occurrence, form);
        toast("Deze en volgende aangepast");
      } else {
        await saveAppointment(appointment?.id ?? null, form);
        toast(appointment ? "Afspraak opgeslagen" : `Afspraak op ${displayDate(form.date)}`);
      }
    } catch (e) {
      console.error(e);
      setError("Opslaan is niet gelukt. Probeer het opnieuw.");
      return;
    }
    close();
  }

  const title = !appointment ? "Nieuwe afspraak" : seriesMode && repeating ? "Herhaling aanpassen" : "Afspraak";
  const input = "min-h-12 rounded-xl border-2 border-mist bg-paper px-3 text-ink";

  return (
    <Sheet open onOpenChange={(o) => !o && close()} title={title}>
      <form
        onSubmit={(e) => submit(e, occurrence && repeating && !seriesMode ? "this" : occurrence && repeating ? "following" : "series")}
        className="flex flex-col gap-4"
      >
        <label className="flex flex-col gap-1">
          <span className="font-semibold">Titel</span>
          <input
            value={form.title}
            maxLength={300}
            onChange={(e) => set("title", e.target.value)}
            className={input}
            autoFocus={!appointment}
          />
        </label>
        <div className="grid grid-cols-2 gap-2">
          <label className="flex flex-col gap-1">
            <span className="font-semibold">Datum</span>
            <input type="date" value={form.date} onChange={(e) => set("date", e.target.value)} className={input} />
          </label>
          <label className="flex flex-col gap-1">
            <span className="font-semibold">Tijd</span>
            <input type="time" value={form.time} onChange={(e) => set("time", e.target.value)} className={input} />
          </label>
        </div>
        <label className="flex flex-col gap-1">
          <span className="font-semibold">Duur</span>
          <select value={form.durationMinutes} onChange={(e) => set("durationMinutes", Number(e.target.value))} className={input}>
            {[...new Set([...DURATIONS, form.durationMinutes])]
              .sort((a, b) => a - b)
              .map((m) => (
                <option key={m} value={m}>
                  {durationLabel(m)}
                </option>
              ))}
          </select>
        </label>
        <label className="flex flex-col gap-1">
          <span className="font-semibold">Plek</span>
          <input value={form.location} maxLength={300} onChange={(e) => set("location", e.target.value)} className={input} />
        </label>
        {other && (
          <fieldset className="flex flex-col gap-2">
            <legend className="mb-1 font-semibold">Wat ziet {other.name}</legend>
            <div role="radiogroup" aria-label={`Wat ziet ${other.name}`} className="grid grid-cols-2 gap-2">
              {WHO.map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  role="radio"
                  aria-checked={form.who === value}
                  onClick={() => set("who", value)}
                  className={cn(
                    "min-h-12 rounded-xl border-2 px-2 font-semibold",
                    form.who === value ? "border-pine bg-pine text-paper" : "border-mist bg-paper text-ink",
                  )}
                >
                  {label}
                </button>
              ))}
            </div>
            <p className="text-sm text-ink-soft">
              {form.who === "private"
                ? `${other.name} ziet niets van deze afspraak.`
                : form.who === "busy"
                  ? `${other.name} ziet alleen dat je bezet bent.`
                  : form.who === "shared"
                    ? `${other.name} ziet de afspraak, maar kan hem niet wijzigen.`
                    : `In jullie gedeelde agenda: allebei zien en wijzigen, allebei een herinnering.`}
            </p>
          </fieldset>
        )}
        {changedBy && <p className="text-sm font-semibold text-pine">Gewijzigd door {changedBy}</p>}

        {seriesMode && (
          <>
            <fieldset className="flex flex-col gap-2">
              <legend className="mb-1 font-semibold">Herhaling</legend>
              <div className="grid grid-cols-3 gap-1">
                {(
                  [
                    ["none", "Geen"],
                    ["weekly", "Wekelijks"],
                    ["monthly", "Maandelijks"],
                  ] as const
                ).map(([kind, label]) => (
                  <button
                    key={kind}
                    type="button"
                    aria-pressed={form.repeat.kind === kind}
                    onClick={() =>
                      setRepeat({
                        kind,
                        weekdays: kind === "weekly" && !form.repeat.weekdays.length ? [isoWeekdayOf(form.date)] : form.repeat.weekdays,
                      })
                    }
                    className={cn(
                      "min-h-12 rounded-xl border-2 px-1",
                      form.repeat.kind === kind ? "border-pine bg-pine text-paper" : "border-mist",
                    )}
                  >
                    {label}
                  </button>
                ))}
              </div>
              {form.repeat.kind === "weekly" && (
                <div className="grid grid-cols-7 gap-0.5" role="group" aria-label="Dagen">
                  {WEEKDAYS.map((d, i) => {
                    const on = form.repeat.weekdays.includes(i + 1);
                    return (
                      <button
                        key={d}
                        type="button"
                        aria-pressed={on}
                        onClick={() =>
                          setRepeat({ weekdays: on ? form.repeat.weekdays.filter((x) => x !== i + 1) : [...form.repeat.weekdays, i + 1] })
                        }
                        className={cn("min-h-12 rounded-lg border-2", on ? "border-pine bg-pine text-paper" : "border-mist")}
                      >
                        {d}
                      </button>
                    );
                  })}
                </div>
              )}
              {form.repeat.kind !== "none" && (
                <div className="grid grid-cols-2 gap-2">
                  <label className="flex flex-col gap-1">
                    <span className="text-sm text-ink-soft">
                      {form.repeat.kind === "weekly" ? "Elke zoveel weken" : "Elke zoveel maanden"}
                    </span>
                    <input
                      type="number"
                      min={1}
                      max={52}
                      value={form.repeat.interval}
                      onChange={(e) => setRepeat({ interval: Math.max(1, Number(e.target.value) || 1) })}
                      className={input}
                    />
                  </label>
                  <label className="flex flex-col gap-1">
                    <span className="text-sm text-ink-soft">Tot en met (leeg is altijd)</span>
                    <input
                      type="date"
                      value={form.repeat.until ?? ""}
                      min={form.date}
                      onChange={(e) => setRepeat({ until: e.target.value || null })}
                      className={input}
                    />
                  </label>
                </div>
              )}
            </fieldset>
            <label className="flex flex-col gap-1">
              <span className="font-semibold">Hoe lang onderweg</span>
              <select value={form.travelMinutes} onChange={(e) => set("travelMinutes", Number(e.target.value))} className={input}>
                {[...new Set([...TRAVEL, form.travelMinutes])]
                  .sort((a, b) => a - b)
                  .map((m) => (
                    <option key={m} value={m}>
                      {m === 0 ? "Geen" : m === 60 ? "1 uur" : m === 90 ? "1,5 uur" : `${m} minuten`}
                    </option>
                  ))}
              </select>
              {form.travelMinutes > 0 && <span className="text-sm text-ink-soft">Je krijgt een melding wanneer je moet vertrekken.</span>}
            </label>
            <label className="flex flex-col gap-1">
              <span className="font-semibold">{form.travelMinutes > 0 ? "Melding voor vertrek" : "Melding vooraf"}</span>
              <select value={form.remindMinutes} onChange={(e) => set("remindMinutes", Number(e.target.value))} className={input}>
                {[...new Set([...REMINDERS, form.remindMinutes])]
                  .sort((a, b) => a - b)
                  .map((m) => (
                    <option key={m} value={m}>
                      {m === 0 ? "Bij de start" : m === 60 ? "1 uur vooraf" : `${m} minuten vooraf`}
                    </option>
                  ))}
              </select>
            </label>
            <label className="flex flex-col gap-1">
              <span className="font-semibold">Notitie</span>
              <textarea
                value={form.notes}
                rows={2}
                maxLength={5000}
                onChange={(e) => set("notes", e.target.value)}
                className={cn(input, "py-2")}
              />
            </label>
          </>
        )}

        {error && (
          <p role="alert" className="text-danger">
            {error}
          </p>
        )}

        {occurrence && repeating && !seriesMode ? (
          <div className="flex flex-col gap-2">
            <Button type="submit">Alleen deze keer opslaan</Button>
            <Button type="button" variant="secondary" onClick={(e) => submit(e as unknown as FormEvent, "following")}>
              Deze en volgende opslaan
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={async () => {
                await skipOccurrence(occurrence);
                toast("Deze keer overgeslagen");
                close();
              }}
            >
              Deze keer overslaan
            </Button>
            <Button type="button" variant="quiet" onClick={() => setSeriesMode(true)}>
              Herhaling, melding of notitie aanpassen
            </Button>
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            <Button type="submit">{occurrence && repeating ? "Opslaan voor deze en volgende" : "Opslaan"}</Button>
            {appointment && (
              <Button
                type="button"
                variant="secondary"
                onClick={async () => {
                  if (!window.confirm(repeating ? "De hele reeks verwijderen?" : "Deze afspraak verwijderen?")) return;
                  await removeAppointment(appointment.id);
                  toast("Afspraak verwijderd");
                  close();
                }}
              >
                {repeating ? "Hele reeks verwijderen" : "Verwijderen"}
              </Button>
            )}
          </div>
        )}
      </form>
    </Sheet>
  );
}
